import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes, FaSearch } from 'react-icons/fa';
import { useWordPress } from '../../hooks/useWordPress';
import './Header.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');

  const searchInputRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { useSearchPosts } = useWordPress();
  const { data: searchData, isLoading: searchLoading } = useSearchPosts(debouncedTerm, 1, 6);

  // Scroll detection
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu and search on route change
  useEffect(() => {
    setIsMenuOpen(false);
    closeSearch();
  }, [location]);

  // Debounce search term (300 ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Focus input when search opens
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  // Close search on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeSearch();
    };
    if (isSearchOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isSearchOpen]);

  const openSearch = () => {
    setIsSearchOpen(true);
    setIsMenuOpen(false);
  };

  const closeSearch = () => {
    setIsSearchOpen(false);
    setSearchTerm('');
    setDebouncedTerm('');
  };

  const handleResultClick = (slug) => {
    navigate(`/blog/${slug}`);
    closeSearch();
  };

  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Return a ~120-char snippet from the content around where the term first appears.
  // Falls back to the start of the content if no match is found.
  const getContextSnippet = (content, term) => {
    const plain = stripHtml(content);
    const idx = plain.toLowerCase().indexOf(term.toLowerCase());
    if (idx === -1) return plain.substring(0, 120);
    const start = Math.max(0, idx - 50);
    const snippet = plain.substring(start, start + 120).trim();
    return (start > 0 ? '\u2026' : '') + snippet;
  };

  const searchResults = searchData?.posts || [];

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/blog', label: 'Blog' },
    { path: '/dive-sites', label: 'Dive Sites' },
    // { path: '/dives', label: 'Dives' }, // hidden — re-add when ready
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' },
  ];

  return (
    <>
      <header className={`header ${isScrolled ? 'header-scrolled' : ''}`}>
        <div className="container">
          <div className="header-content">
            <Link to="/" className="logo">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="logo-text">🤿 Nothing But Scuba</span>
              </motion.div>
            </Link>

            <nav className={`nav ${isMenuOpen ? 'nav-open' : ''}`}>
              <ul className="nav-list">
                {navItems.map((item) => (
                  <li key={item.path} className="nav-item">
                    <Link
                      to={item.path}
                      className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="header-actions">
              <button
                className={`search-btn ${isSearchOpen ? 'search-btn-active' : ''}`}
                aria-label={isSearchOpen ? 'Close search' : 'Open search'}
                onClick={isSearchOpen ? closeSearch : openSearch}
              >
                {isSearchOpen ? <FaTimes /> : <FaSearch />}
              </button>
              <button
                className="menu-toggle"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Toggle menu"
              >
                {isMenuOpen ? <FaTimes /> : <FaBars />}
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              className="mobile-menu"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="container">
                <ul className="mobile-nav-list">
                  {navItems.map((item) => (
                    <li key={item.path} className="mobile-nav-item">
                      <Link
                        to={item.path}
                        className={`mobile-nav-link ${location.pathname === item.path ? 'active' : ''}`}
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Search overlay — outside <header> so backdrop covers the full page */}
      <AnimatePresence>
        {isSearchOpen && (
          <>
            <motion.div
              className="search-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={closeSearch}
            />
            <motion.div
              className="search-panel"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
            >
              <div className="container">
                <div className="search-input-wrapper">
                  <FaSearch className="search-input-icon" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    className="search-input"
                    placeholder="Search posts..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button
                      className="search-clear-btn"
                      onClick={() => {
                        setSearchTerm('');
                        setDebouncedTerm('');
                        searchInputRef.current?.focus();
                      }}
                      aria-label="Clear search"
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>

                <AnimatePresence>
                  {debouncedTerm.length > 2 && (
                    <motion.div
                      className="search-results-panel"
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.15 }}
                    >
                      {searchLoading && (
                        <div className="search-status">Searching...</div>
                      )}
                      {!searchLoading && searchResults.length === 0 && (
                        <div className="search-status">
                          No results for &ldquo;{debouncedTerm}&rdquo;
                        </div>
                      )}
                      {!searchLoading && searchResults.length > 0 && (
                        <ul className="search-results-list">
                          {searchResults.map((post) => {
                            const featuredImage =
                              post._embedded?.['wp:featuredmedia']?.[0]?.source_url;
                            const excerpt = getContextSnippet(
                              post.content?.rendered || post.excerpt?.rendered || '',
                              debouncedTerm
                            );
                            return (
                              <li key={post.id} className="search-result-item">
                                <button
                                  className="search-result-btn"
                                  onClick={() => handleResultClick(post.slug)}
                                >
                                  {featuredImage && (
                                    <img
                                      src={featuredImage}
                                      alt=""
                                      className="search-result-img"
                                    />
                                  )}
                                  <div className="search-result-text">
                                    <span
                                      className="search-result-title"
                                      dangerouslySetInnerHTML={{ __html: post.title.rendered }}
                                    />
                                    {excerpt && (
                                      <span className="search-result-excerpt">
                                        {excerpt}\u2026
                                      </span>
                                    )}
                                  </div>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header; 